import { useCallback, useEffect, useState } from 'react';
import api from '../../services';
import { normalizeExternalUrl } from '../../utils/externalLinkText';
import { internalContentTarget } from '../../appLinks/links';

const MAX_ENTRIES = 128;
const MAX_CONCURRENT = 3;
const cache = new Map();
const listeners = new Map();
const pending = { status: 'pending', provider: 'web' };
const queue = [];
let activeRequests = 0;

function internalPreview(url) {
  return internalContentTarget(url) ? { url, provider: 'web', status: 'ready', safe: true } : null;
}

function currentPreview(url) {
  const internal = internalPreview(url);
  if (internal) return internal;
  const entry = cache.get(url);
  return entry?.expires > Date.now() && entry.value ? entry.value : pending;
}

function releaseRequestSlot() {
  activeRequests -= 1;
  drainQueue();
}

function drainQueue() {
  while (activeRequests < MAX_CONCURRENT && queue.length) {
    const job = queue.shift();
    activeRequests += 1;
    Promise.resolve().then(() => api.linkPreview?.({ url: job.url }))
      .then(job.resolve, () => job.resolve(null))
      .finally(releaseRequestSlot);
  }
}

function requestPreview(url) {
  return new Promise(resolve => {
    if (queue.length >= MAX_ENTRIES) { resolve(null); return; }
    queue.push({ url, resolve });
    drainQueue();
  });
}

export function validatedLinkUrl(value) {
  const normalized = normalizeExternalUrl(value);
  if (!normalized) return null;
  const url = new URL(normalized);
  return url.username || url.password ? null : normalized;
}

function resultFor(url, response) {
  const link = response?.success && response.link;
  if (!link || !['ready', 'blocked', 'unavailable'].includes(link.status)) {
    return { url, provider: 'web', status: 'unavailable', safe: false };
  }
  const destination = validatedLinkUrl(link.url);
  if (!destination) return { url, provider: 'web', status: 'blocked', reason: 'invalid' };
  return { ...link, url: destination, safe: link.status === 'ready' || link.safe === true,
    provider: ['youtube', 'spotify', 'appleMusic', 'soundcloud'].includes(link.provider) ? link.provider : 'web' };
}

function publish(url, value) {
  listeners.get(url)?.forEach(listener => listener(value));
}

export function loadLinkPreview(value, force = false) {
  const url = validatedLinkUrl(value);
  if (!url) return Promise.resolve({ status: 'blocked', reason: 'invalid', provider: 'web' });
  // Our own content loads through the authenticated router and its normal
  // content permissions. Do not bounce it through an outbound preview fetch.
  const internal = internalPreview(url);
  if (internal) return Promise.resolve(internal);
  const existing = cache.get(url);
  if (existing?.promise) return existing.promise;
  if (!force && existing?.expires > Date.now()) return Promise.resolve(existing.value);
  // Keep pending entries deduplicated until completion, including after unmount.
  // At capacity, reject a new request safely rather than growing a hidden queue.
  cache.delete(url);
  while (cache.size >= MAX_ENTRIES) {
    const settled = [...cache].find(([, item]) => !item.promise);
    if (!settled) return Promise.resolve(resultFor(url, null));
    cache.delete(settled[0]);
  }
  const entry = {};
  cache.set(url, entry);
  publish(url, pending);
  entry.promise = requestPreview(url)
    .then(response => resultFor(url, response), () => resultFor(url, null))
    .then(result => {
      entry.value = result;
      entry.expires = Date.now() + (result.status === 'ready' ? 300000 : result.status === 'blocked' ? 60000 : 15000);
      entry.promise = null;
      publish(url, result);
      return result;
    });
  return entry.promise;
}

export function clearLinkPreviewCache() { cache.clear(); }

export default function useLinkPreview(href) {
  const url = validatedLinkUrl(href);
  const [state, setState] = useState(() => ({ url, value: currentPreview(url) }));
  useEffect(() => {
    let mounted = true;
    const update = value => { if (mounted) setState({ url, value }); };
    if (!listeners.has(url)) listeners.set(url, new Set());
    listeners.get(url).add(update);
    loadLinkPreview(url).then(update);
    return () => {
      mounted = false;
      listeners.get(url)?.delete(update);
      if (!listeners.get(url)?.size) listeners.delete(url);
    };
  }, [url]);
  const retry = useCallback(() => loadLinkPreview(url, true), [url]);
  return { link: state.url === url ? state.value : currentPreview(url), retry };
}
